"use client";

import { useState, useRef, useEffect, useCallback, useTransition } from "react";
import { useRouter } from "next/navigation";
import SearchDropdown from "./SearchDropdown";
import { buildSearchUrl } from "@/lib/url-state";
import { clientCompletionUrl } from "@/lib/api-url";
import { trackSearchSubmit } from "@/lib/analytics";

interface SearchBarProps {
  placeholder?: string;
  defaultValue?: string;
  className?: string;
  /** If provided, called on Enter instead of the default navigation */
  onSearch?: (query: string) => void;
}

interface AutocompleteResult {
  id: string;
  result: string; // BBCode format: "PartNum - Brand - Description" with [b]...[/b] highlights
}

export default function SearchBar({
  placeholder = "Search",
  defaultValue = "",
  className = "",
  onSearch,
}: SearchBarProps) {
  const router = useRouter();
  const [, startTransition] = useTransition();
  const [query, setQuery] = useState(defaultValue);
  const [results, setResults] = useState<AutocompleteResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const fetchAutocomplete = useCallback(async (search: string) => {
    if (!search.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(clientCompletionUrl(search));
      if (res.ok) {
        const data = await res.json();
        startTransition(() => {
          setResults(Array.isArray(data) ? data : []);
          setIsOpen(true);
        });
      }
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAutocomplete(value);
    }, 300);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && query.trim()) {
      setIsOpen(false);
      if (onSearch) {
        onSearch(query.trim());
      } else {
        trackSearchSubmit(query.trim(), "header");
        // Preserve existing URL params (vehicle_key, brand_name, etc.)
        const currentParams = new URLSearchParams(window.location.search);
        const url = buildSearchUrl(currentParams, { refine_search: query.trim() });
        window.location.href = url;
      }
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  }

  const handleSuggestionClick = useCallback((result: AutocompleteResult) => {
    setIsOpen(false);
    setQuery("");
    const url = `/product/${result.id}`;
    window.location.href = url;
  }, [router]);

  function handleClear() {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { router.prefetch("/products"); if (results.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          className="w-full rounded border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-base text-black placeholder-gray-400 outline-none focus:border-gray-400"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            aria-label="Clear search"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && (
        <SearchDropdown
          results={results}
          query={query}
          isLoading={isLoading}
          onSelect={handleSuggestionClick}
        />
      )}
    </div>
  );
}
